export type ModoAtendimento = "mesa" | "comanda" | "ambos";

export type AtendimentoLabels = {
  singular: "Mesa" | "Comanda";
  plural: "Mesas" | "Comandas";
  singularLower: "mesa" | "comanda";
  pluralLower: "mesas" | "comandas";
  numeroLabel: "Nº Mesa" | "Nº Comanda";
  abrir: "Abrir Mesa" | "Abrir Comanda";
  abertas: "Mesas abertas" | "Comandas abertas";
  adicionar: "Adicionar à mesa" | "Adicionar à comanda";
  editando: "Editando mesa" | "Editando comanda";
  jaExiste: string;
  destino: "à mesa" | "à comanda";
};

const MESA_LABELS: AtendimentoLabels = {
  singular: "Mesa",
  plural: "Mesas",
  singularLower: "mesa",
  pluralLower: "mesas",
  numeroLabel: "Nº Mesa",
  abrir: "Abrir Mesa",
  abertas: "Mesas abertas",
  adicionar: "Adicionar à mesa",
  editando: "Editando mesa",
  jaExiste: "Já existe uma mesa aberta com essa identificação.",
  destino: "à mesa",
};

const COMANDA_LABELS: AtendimentoLabels = {
  singular: "Comanda",
  plural: "Comandas",
  singularLower: "comanda",
  pluralLower: "comandas",
  numeroLabel: "Nº Comanda",
  abrir: "Abrir Comanda",
  abertas: "Comandas abertas",
  adicionar: "Adicionar à comanda",
  editando: "Editando comanda",
  jaExiste: "Já existe uma comanda aberta com essa identificação.",
  destino: "à comanda",
};

export function normalizeModoAtendimento(value: unknown): ModoAtendimento {
  if (typeof value === "string" && value.trim().toLowerCase() === "ambos") return "ambos";
  return typeof value === "string" && value.trim().toLowerCase() === "comanda" ? "comanda" : "mesa";
}

export function getAtendimentoLabels(mode: ModoAtendimento): AtendimentoLabels {
  return mode !== "mesa" ? COMANDA_LABELS : MESA_LABELS;
}

export function normalizeAtendimentoNome(value: string | null | undefined): string {
  return (value ?? "").trim();
}

export function limiteAtendimentoNumero(mode: ModoAtendimento = "mesa"): number {
  return mode !== "mesa" ? 5 : 3;
}

export function normalizeAtendimentoNumero(
  value: string | null | undefined,
  mode: ModoAtendimento = "mesa",
): string {
  const digits = (value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (mode !== "mesa") return digits;
  const n = parseInt(digits, 10);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
}

export function filterAtendimentoNumeroInput(value: string, mode: ModoAtendimento = "mesa"): string {
  return value.replace(/\D/g, "").slice(0, limiteAtendimentoNumero(mode));
}

export function buildAtendimentoIdentificacao(
  mode: ModoAtendimento,
  nomeRaw: string | null | undefined,
  numeroRaw: string | null | undefined,
  mesaRaw?: string | null,
): string {
  if (mode === "ambos") return formatAtendimentoAmbos(nomeRaw, numeroRaw, mesaRaw);
  const nome = normalizeAtendimentoNome(nomeRaw);
  const numero = normalizeAtendimentoNumero(numeroRaw, mode);
  const labels = getAtendimentoLabels(mode);

  if (numero && nome) return `${labels.singular} ${numero} - ${nome}`;
  if (numero) return `${labels.singular} ${numero}`;
  return nome;
}

export function formatAtendimentoDisplay(
  mode: ModoAtendimento,
  nomeRaw: string | null | undefined,
  numeroRaw: string | null | undefined,
  mesaRaw?: string | null,
): string {
  if (mode === "ambos") return formatAtendimentoAmbos(nomeRaw, numeroRaw, mesaRaw);
  const nome = normalizeAtendimentoNome(nomeRaw);
  const numero = normalizeAtendimentoNumero(numeroRaw, mode);
  const labels = getAtendimentoLabels(mode);

  if (numero && nome) return `${labels.singular} ${numero} - ${nome}`;
  if (numero) return numero;
  return nome;
}

export function parseAtendimentoIdentificacao(
  value: string | null | undefined,
  mode: ModoAtendimento = "mesa",
): { nome: string; numero: string; mesa?: string } {
  const trimmed = normalizeAtendimentoNome(value);
  if (mode === "ambos") {
    const pieces = trimmed.split(" · ");
    const numero = trimmed.match(/(?:^| · )Comanda:\s*(\d+)(?= · |$)/i)?.[1] ?? "";
    const mesa = trimmed.match(/(?:^| · )Mesa:\s*(\d+)(?= · |$)/i)?.[1] ?? "";
    if (numero || mesa) return {
      nome: /^(Comanda|Mesa):/i.test(pieces[0]) ? "" : pieces[0],
      numero: normalizeAtendimentoNumero(numero, "comanda"),
      mesa: normalizeAtendimentoNumero(mesa, "mesa"),
    };
    const legacy = parseAtendimentoIdentificacao(value, "comanda");
    return /^Mesa\s+/i.test(trimmed)
      ? { nome: legacy.nome, numero: "", mesa: normalizeAtendimentoNumero(legacy.numero, "mesa") }
      : { ...legacy, mesa: "" };
  }
  if (!trimmed) return { nome: "", numero: "" };

  const match = trimmed.match(/^(?:(Mesa|Comanda)\s+)?(\d+)(?:\s*-\s*(.*))?$/i);
  if (!match) return { nome: trimmed, numero: "" };

  const parsedMode = match[1]?.toLowerCase() === "comanda"
    ? "comanda"
    : match[1]?.toLowerCase() === "mesa"
      ? "mesa"
      : mode;

  return {
    nome: normalizeAtendimentoNome(match[3]),
    numero: normalizeAtendimentoNumero(match[2], parsedMode),
  };
}

export function formatAtendimentoAmbos(
  nome?: string | null, numeroComanda?: string | null, numeroMesa?: string | null,
): string {
  const comanda = normalizeAtendimentoNumero(numeroComanda, "comanda");
  const mesa = normalizeAtendimentoNumero(numeroMesa, "mesa");
  return [normalizeAtendimentoNome(nome), comanda ? `Comanda: ${comanda}` : "", mesa ? `Mesa: ${mesa}` : ""].filter(Boolean).join(" · ");
}
